import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import Header from "../../components/ui/Header";
import Footer from "../../components/ui/Footer";

import ServiceFilter from "./components/ServiceFilter";
import ServiceCard from "./components/ServiceCard";
import ServiceModal from "./components/ServiceModal";
import ServiceComparison from "./components/ServiceComparison";
import TechnologyPartners from "./components/TechnologyPartners";
import CTASection from "./components/CTASection";
import Icon from "../../components/AppIcon";

const Services = () => {
  const [activeFilter, setActiveFilter] = useState("all");
  const [activeIndustry, setActiveIndustry] = useState("all");
  const [selectedService, setSelectedService] = useState(null);
  const [filteredServices, setFilteredServices] = useState([]);

  /* ================= SERVICES DATA ================= */
  /* ⚠️ YOUR FULL SERVICES ARRAY REMAINS EXACTLY SAME */
  const services = [
    {
      id: 1,
      title: "App Development",
      category: "IT Solutions",
      icon: "Mobile",
      image: "assets/images/services/App_Development.png",
      imageAlt:
        "Mobile app development interface showing cross-platform coding on multiple devices",
      description:
        "Custom mobile and web application development using modern frameworks for iOS, Android, and cross-platform solutions.",
      fullDescription:
        "Our App Development services deliver high-performance, scalable mobile and web applications tailored to your business needs. With expertise in React Native, Flutter, and native development, we create intuitive user experiences that drive engagement and retention. From concept to deployment, we handle the full lifecycle with agile methodologies.",
      features: [
        "iOS & Android Native Development",
        "Cross-Platform Solutions (React Native, Flutter)",
        "UI/UX Design & Prototyping",
        "Backend Integration & APIs",
        "App Store Optimization",
        "Performance Testing & Optimization",
        "Push Notifications & Analytics",
        "Ongoing Maintenance & Updates",
      ],
      roi: "250% user engagement increase",
      timeline: "8-12 weeks",
      clientCount: 72,
      technologies: [
        "React Native",
        "Flutter",
        "Swift",
        "Kotlin",
        "Node.js",
        "Firebase",
      ],
      industries: ["startup", "saas", "enterprise", "healthcare"],
      serviceType: "it",
      caseStudy: {
        clientName: "HealthTrack Apps",
        clientLogo:
          "https://img.rocket.new/generatedImages/rocket_gen_img_1cf659784-1764649751680.png",
        clientLogoAlt:
          "Healthcare app interface on smartphone showing patient tracking features",
        industry: "Healthcare",
        challenge:
          "HealthTrack needed a cross-platform mobile app to track patient vitals and enable remote consultations while ensuring HIPAA compliance.",
        results: [
          { value: "45K+", metric: "Downloads" },
          { value: "4.7", metric: "App Rating" },
          { value: "78%", metric: "User Retention" },
        ],
      },
    },
    {
      id: 2,
      title: "Cloud Migrations",
      category: "Cloud Solutions",
      icon: "Cloud",
      image: "assets/images/services/Cloud_Migrations.png",
      imageAlt:
        "Digital cloud computing visualization with interconnected nodes and data streams in blue and purple gradient",
      description:
        "Seamless migration to AWS, Azure, or Google Cloud with ongoing management, optimization, and cost control strategies.",
      fullDescription:
        "Transform your business with our comprehensive Cloud Migration services. We specialize in moving enterprise workloads to leading cloud platforms while ensuring zero downtime and data integrity. Our team handles architecture design, migration execution, security configuration, and ongoing optimization to maximize your cloud investment ROI.",
      features: [
        "Multi-Cloud Strategy Development",
        "AWS/Azure/GCP Migration",
        "Data Migration & Validation",
        "Cloud Security Configuration",
        "Cost Optimization Analysis",
        "Performance Monitoring",
        "Hybrid Cloud Setup",
        "Compliance Management",
      ],
      roi: "40% infrastructure cost reduction",
      timeline: "6-8 weeks",
      clientCount: 64,
      technologies: [
        "AWS",
        "Azure",
        "Google Cloud",
        "Kubernetes",
        "Docker",
        "Terraform",
      ],
      industries: ["enterprise", "saas", "startup"],
      serviceType: "cloud",
      caseStudy: {
        clientName: "FinServe Solutions",
        clientLogo:
          "https://img.rocket.new/generatedImages/rocket_gen_img_16cfd1832-1766499990903.png",
        clientLogoAlt:
          "Financial district skyline with modern skyscrapers representing FinServe Solutions banking sector",
        industry: "Financial Services",
        challenge:
          "FinServe needed to migrate critical banking applications to the cloud while maintaining strict regulatory compliance and ensuring zero data loss during transition.",
        results: [
          { value: "45%", metric: "Cost Savings" },
          { value: "100%", metric: "Data Integrity" },
          { value: "5x", metric: "Scalability" },
        ],
      },
    },
    {
      id: 3,
      title: "Cyber Security",
      category: "Security",
      icon: "Shield",
      image: "assets/images/services/Cyber_Security.png",
      imageAlt:
        "Digital security shield with glowing blue circuit patterns protecting against cyber threats in dark background",
      description:
        "Comprehensive cybersecurity services including threat detection, penetration testing, compliance audits, and security training.",
      fullDescription:
        "Protect your business with our enterprise-grade Cyber Security solutions. We provide multi-layered security strategies combining advanced threat detection, proactive monitoring, and employee training. Our certified security experts conduct regular assessments and implement industry best practices to safeguard your digital assets against evolving cyber threats.",
      features: [
        "24/7 Threat Monitoring",
        "Penetration Testing",
        "Security Audits & Compliance",
        "Incident Response Planning",
        "Employee Security Training",
        "Firewall Configuration",
        "Vulnerability Assessment",
        "Data Encryption Solutions",
      ],
      roi: "60% risk reduction",
      timeline: "3-4 weeks",
      clientCount: 92,
      technologies: [
        "Acronis",
        "Palo Alto",
        "Fortinet",
        "CrowdStrike",
        "Splunk",
        "Tenable",
      ],
      industries: ["healthcare", "enterprise", "saas"],
      serviceType: "security",
      caseStudy: {
        clientName: "HealthCare Plus",
        clientLogo:
          "https://img.rocket.new/generatedImages/rocket_gen_img_146ed6590-1764664281973.png",
        clientLogoAlt:
          "Modern hospital building with emergency entrance and medical cross symbol representing HealthCare Plus facility",
        industry: "Healthcare",
        challenge:
          "HealthCare Plus required HIPAA-compliant security infrastructure to protect sensitive patient data while enabling seamless access for medical staff across multiple locations.",
        results: [
          { value: "100%", metric: "Compliance Achieved" },
          { value: "0", metric: "Security Breaches" },
          { value: "70%", metric: "Threat Detection" },
        ],
      },
    },
    {
      id: 4,
      title: "DC-DR Migrations and Implementation Services",
      category: "Cloud Solutions",
      icon: "Database",
      image: "assets/images/services/DC-DR_Migrations.png",
      imageAlt:
        "Data center migration scene with servers being transferred to new infrastructure with backup systems",
      description:
        "Data center migration and disaster recovery planning, implementation, and testing for business continuity.",
      fullDescription:
        "Ensure business continuity with our DC-DR (Data Center - Disaster Recovery) Migrations and Implementation Services. We specialize in seamless data center relocations, hybrid cloud setups, and comprehensive DR strategies that minimize downtime and protect critical data. Our proven methodologies guarantee rapid recovery and compliance with industry standards.",
      features: [
        "Data Center Assessment & Planning",
        "Migration Strategy & Execution",
        "Disaster Recovery Architecture",
        "Failover & Failback Testing",
        "Backup & Replication Solutions",
        "RTO/RPO Optimization",
        "Compliance & Audit Support",
        "Post-Migration Optimization",
      ],
      roi: "50% downtime reduction",
      timeline: "8-12 weeks",
      clientCount: 51,
      technologies: [
        "VMware vSphere",
        "Zerto",
        "Veeam",
        "Cisco UCS",
        "Azure Site Recovery",
        "AWS Disaster Recovery",
      ],
      industries: ["enterprise", "healthcare", "financial"],
      serviceType: "cloud",
      caseStudy: {
        clientName: "GlobalBank Corp",
        clientLogo:
          "https://img.rocket.new/generatedImages/rocket_gen_img_16cfd1832-1766499990903.png",
        clientLogoAlt:
          "Secure banking data center with redundant systems and monitoring consoles",
        industry: "Financial Services",
        challenge:
          "GlobalBank needed to migrate their primary data center to a new facility while implementing DR solutions to meet regulatory recovery time objectives.",
        results: [
          { value: "4 hours", metric: "RTO Achieved" },
          { value: "99.99%", metric: "Availability" },
          { value: "65%", metric: "Cost Optimization" },
        ],
      },
    },
    {
      id: 5,
      title: "Facility Management Services",
      category: "Operations",
      icon: "Building",
      image: "assets/images/services/Facility_Management.png",
      imageAlt:
        "Modern office building management dashboard showing maintenance schedules and energy usage",
      description:
        "Integrated facility management including maintenance, energy optimization, security systems, and space planning.",
      fullDescription:
        "Streamline your facility operations with our comprehensive Facility Management Services. We provide end-to-end solutions for commercial and industrial properties, combining smart building technologies, preventive maintenance, and sustainability practices to reduce costs and enhance occupant experience. Our certified team ensures compliance and operational efficiency.",
      features: [
        "Preventive Maintenance Scheduling",
        "HVAC & Energy Management",
        "Building Automation Systems",
        "Space Planning & Utilization",
        "Security System Integration",
        "Vendor Coordination & Procurement",
        "Compliance & Safety Audits",
        "Sustainability Reporting",
      ],
      roi: "30% operational cost savings",
      timeline: "Ongoing",
      clientCount: 68,
      technologies: [
        "BuildingOS",
        "ServiceNow FM",
        "Johnson Controls",
        "Schneider Electric",
        "AutoCAD",
        "IoT Sensors",
      ],
      industries: ["enterprise", "real estate", "manufacturing"],
      serviceType: "operations",
      caseStudy: {
        clientName: "Corporate Plaza Properties",
        clientLogo:
          "https://img.rocket.new/generatedImages/rocket_gen_img_13526ac82-1764677557597.png",
        clientLogoAlt:
          "Modern corporate office building with smart glass facade and green rooftop",
        industry: "Real Estate",
        challenge:
          "Corporate Plaza needed to optimize energy usage and maintenance across a 500,000 sq ft portfolio while improving tenant satisfaction scores.",
        results: [
          { value: "28%", metric: "Energy Cost Reduction" },
          { value: "92%", metric: "Tenant Satisfaction" },
          { value: "40%", metric: "Maintenance Efficiency" },
        ],
      },
    },
    {
      id: 6,
      title: "Physical Marketing",
      category: "Branding",
      icon: "Megaphone",
      image: "assets/images/services/Physical_Marketing.png",
      imageAlt:
        "Outdoor advertising campaign with billboards, signage, and experiential marketing installations",
      description:
        "Traditional marketing solutions including print materials, signage, outdoor advertising, and experiential campaigns.",
      fullDescription:
        "Amplify your brand presence with our Physical Marketing services that blend traditional advertising with modern execution. From large-format signage and vehicle wraps to experiential events and print collateral, we create tangible brand experiences that drive foot traffic and customer engagement in the physical world.",
      features: [
        "Large Format Printing & Signage",
        "Vehicle & Fleet Branding",
        "Outdoor Advertising (Billboards)",
        "Trade Show & Event Displays",
        "Print Collateral Design",
        "Installation & Project Management",
        "Environmental Graphics",
        "POP/POS Materials",
      ],
      roi: "180% brand visibility increase",
      timeline: "4-6 weeks",
      clientCount: 59,
      technologies: [
        "Adobe Illustrator",
        "CorelDRAW",
        "Large Format Printers",
        "Vinyl Cutting Systems",
        "LED Display Technology",
        "Project Management Software",
      ],
      industries: ["retail", "startup", "enterprise"],
      serviceType: "branding",
      caseStudy: {
        clientName: "Urban Brew Coffee",
        clientLogo:
          "https://img.rocket.new/generatedImages/rocket_gen_img_122b22f9d-1764654516001.png",
        clientLogoAlt:
          "Coffee shop exterior with branded signage and outdoor seating area",
        industry: "Food & Beverage",
        challenge:
          "Urban Brew needed to establish physical brand presence across 15 new locations with consistent signage and promotional materials on a tight budget.",
        results: [
          { value: "35%", metric: "Foot Traffic Increase" },
          { value: "220%", metric: "Local Brand Recognition" },
          { value: "25%", metric: "Material Cost Savings" },
        ],
      },
    },
    {
      id: 7,
      title: "Web Development",
      category: "IT Solutions",
      icon: "Code",
      image: "assets/images/services/Web_Development.png",
      imageAlt:
        "Web development workspace with code editor, browser previews, and responsive design tools",
      description:
        "Custom website development using modern frameworks with focus on performance, SEO, and user experience.",
      fullDescription:
        "Build a powerful online presence with our custom Web Development services. We create responsive, high-performance websites using the latest technologies and best practices. Our development process includes comprehensive planning, intuitive design, rigorous testing, and SEO optimization to ensure your site converts visitors into customers.",
      features: [
        "Responsive Web Design",
        "Custom CMS Development",
        "E-commerce Solutions",
        "API Integration & Backend",
        "SEO & Performance Optimization",
        "Security Implementation",
        "Cross-Browser Testing",
        "Progressive Web Apps",
      ],
      roi: "300% conversion rate improvement",
      timeline: "6-10 weeks",
      clientCount: 89,
      technologies: [
        "React",
        "Next.js",
        "WordPress",
        "Shopify",
        "Node.js",
        "Headless CMS",
      ],
      industries: ["startup", "saas", "enterprise", "retail"],
      serviceType: "it",
      caseStudy: {
        clientName: "EcomTrend Store",
        clientLogo:
          "https://img.rocket.new/generatedImages/rocket_gen_img_122b22f9d-1764654516001.png",
        clientLogoAlt:
          "E-commerce website interface showing product catalog and shopping cart",
        industry: "E-commerce",
        challenge:
          "EcomTrend needed a scalable e-commerce platform that could handle seasonal traffic spikes while providing seamless user experience across devices.",
        results: [
          { value: "420%", metric: "Traffic Growth" },
          { value: "3.2x", metric: "Conversion Rate" },
          { value: "99.9%", metric: "Site Uptime" },
        ],
      },
    },
    {
      id: 8,
      title: "Web Hosting",
      category: "IT Solutions",
      icon: "Globe",
      image: "assets/images/services/Web_Hosting.png",
      imageAlt:
        "Server room with high-availability web hosting infrastructure and monitoring systems",
      description:
        "Reliable web hosting solutions with 99.9% uptime, scalable resources, and 24/7 technical support.",
      fullDescription:
        "Power your digital presence with our robust Web Hosting services designed for performance and reliability. We offer managed hosting solutions across shared, VPS, dedicated, and cloud environments, complete with automatic backups, security monitoring, and expert support. Scale effortlessly as your business grows without compromising speed or security.",
      features: [
        "Managed WordPress Hosting",
        "VPS & Dedicated Servers",
        "Cloud Hosting Solutions",
        "SSL Certificates & Security",
        "Daily Backups & Recovery",
        "Performance Caching",
        "24/7 Expert Support",
        "Easy Migration Services",
      ],
      roi: "99.9% uptime guarantee",
      timeline: "Immediate setup",
      clientCount: 156,
      technologies: [
        "cPanel",
        "Plesk",
        "Cloudflare",
        "LiteSpeed",
        "AWS Hosting",
        "Google Cloud Platform",
      ],
      industries: ["startup", "saas", "enterprise", "small business"],
      serviceType: "it",
      caseStudy: {
        clientName: "ContentHub Media",
        clientLogo:
          "https://img.rocket.new/generatedImages/rocket_gen_img_13913910e-1764639852428.png",
        clientLogoAlt:
          "Media company server rack with high-traffic website analytics dashboard",
        industry: "Media & Publishing",
        challenge:
          "ContentHub experienced frequent downtime during traffic surges and needed a hosting solution that could scale dynamically without performance degradation.",
        results: [
          { value: "99.98%", metric: "Uptime Achieved" },
          { value: "8x", metric: "Traffic Capacity" },
          { value: "45%", metric: "Loading Speed Improvement" },
        ],
      },
    },
    {
      id: 9,
      title: "Zoho Cloud",
      category: "Cloud Solutions",
      icon: "CloudCheck",
      image: "assets/images/services/Zoho_Cloud.png",
      imageAlt:
        "Zoho One dashboard showing integrated CRM, email, and project management tools",
      description:
        "Zoho One implementation and customization for CRM, collaboration, finance, and HR management.",
      fullDescription:
        "Maximize productivity with our Zoho Cloud implementation services. As certified Zoho partners, we help businesses implement and customize the Zoho One suite to streamline operations across sales, marketing, support, HR, and finance. Our tailored implementations ensure seamless integration with existing systems and comprehensive user training.",
      features: [
        "Zoho One Full Suite Implementation",
        "CRM Customization & Migration",
        "Workflow Automation Setup",
        "Integration with Third-Party Apps",
        "User Training & Adoption",
        "Custom Reports & Dashboards",
        "Data Migration Services",
        "Ongoing Support & Optimization",
      ],
      roi: "40% productivity improvement",
      timeline: "4-8 weeks",
      clientCount: 47,
      technologies: [
        "Zoho CRM",
        "Zoho Books",
        "Zoho Projects",
        "Zoho Mail",
        "Deluge Scripting",
        "Zoho Analytics",
      ],
      industries: ["startup", "saas", "enterprise", "professional services"],
      serviceType: "cloud",
      caseStudy: {
        clientName: "ProServe Consulting",
        clientLogo:
          "https://img.rocket.new/generatedImages/rocket_gen_img_1528b020c-1764648555488.png",
        clientLogoAlt:
          "Professional services office with Zoho dashboards on multiple screens",
        industry: "Professional Services",
        challenge:
          "ProServe needed to consolidate multiple SaaS tools into a single platform to reduce costs and improve team collaboration across remote locations.",
        results: [
          { value: "38%", metric: "Cost Reduction" },
          { value: "65%", metric: "Process Efficiency" },
          { value: "95%", metric: "User Adoption Rate" },
        ],
      },
    },
  ];

  useEffect(() => {
    let filtered = services;

    if (activeFilter !== "all") {
      filtered = filtered.filter(
        (service) => service.serviceType === activeFilter
      );
    }

    if (activeIndustry !== "all") {
      filtered = filtered.filter((service) =>
        service.industries.includes(activeIndustry)
      );
    }

    setFilteredServices(filtered);
  }, [activeFilter, activeIndustry]);

  const handleLearnMore = (service) => setSelectedService(service);
  const closeModal = () => setSelectedService(null);

  return (
    <>
      <Helmet>
        <title>Services - Orator Hype | IT & Branding Solutions</title>
        <meta
          name="description"
          content="Comprehensive IT infrastructure, cloud migration, cybersecurity, app development, web hosting, and branding services."
        />
      </Helmet>

      <div className="min-h-screen bg-[#0b1d3a] text-white">
        <main>
          {/* ================= HERO + HEADER ================= */}
          <section className="relative min-h-screen overflow-hidden">
            {/* Background Image */}
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: "url('/hero_section/Our_Services.png')",
              }}
            />

            {/* Dark overlay (NOT gradient) */}
            <div className="absolute inset-0 bg-black/50" />

            {/* HEADER ON IMAGE */}
            <div className="relative z-50">
              <Header />
            </div>

            {/* HERO CONTENT */}
            <div className="relative z-10 flex items-center min-h-screen px-4 md:px-6 lg:px-8">
              <div className="max-w-7xl mx-auto text-center pt-32">
                <div className="inline-flex bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full mb-8">
                  <Icon name="Zap" size={24} className="text-white mr-2" />
                  <span className="text-sm font-semibold uppercase tracking-wider">
                    Our Expertise
                  </span>
                </div>

                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                  Our Services
                </h1>

                <p className="text-xl md:text-2xl lg:text-3xl text-gray-200 max-w-3xl mx-auto">
                  Comprehensive IT & Branding Solutions That Drive Business
                  Growth
                </p>

                <div className="mt-12 flex flex-wrap justify-center gap-6">
                  {[
                    { value: "10+", label: "Premium Services" },
                    { value: "196+", label: "Happy Clients" },
                    { value: "13+", label: "Years Experience" },
                    { value: "24/7", label: "Support Available" },
                  ].map((item, i) => (
                    <div key={i} className="px-6 py-4">
                      <div className="text-3xl font-bold">{item.value}</div>
                      <div className="text-gray-300">{item.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* ================= SERVICES GRID ================= */}
          <section className="py-12 md:py-16 lg:py-20 px-4 md:px-6 lg:px-8 bg-[#0b1d3a]">
            <div className="max-w-7xl mx-auto">
              <div className="mb-12 text-center">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
                  Explore Our Service Portfolio
                </h2>
                <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto">
                  From app development to facility management, we provide
                  end-to-end solutions tailored to your business needs
                </p>
              </div>

              <ServiceFilter
                activeFilter={activeFilter}
                onFilterChange={setActiveFilter}
                activeIndustry={activeIndustry}
                onIndustryChange={setActiveIndustry}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {filteredServices.map((service) => (
                  <ServiceCard
                    key={service.id}
                    service={service}
                    onLearnMore={handleLearnMore}
                  />
                ))}
              </div>
            </div>
          </section>

          <ServiceComparison />
          <TechnologyPartners />
          <CTASection />
        </main>

        <Footer />

        {selectedService && (
          <ServiceModal service={selectedService} onClose={closeModal} />
        )}
      </div>
    </>
  );
};

export default Services;
